import React, {Component} from 'react';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import _ from 'lodash';

import {formatKmbt} from '../utils/d3-mixins'

class LineChartGrid extends Component {

  shouldComponentUpdate(nextProps) {
    const widthDifferent = nextProps.width !== this.props.width;
    const heightDifferent = nextProps.height !== this.props.height;
    const dataDifferent = nextProps.data !== this.props.data;
    const dimYDiff = nextProps.dimY !== this.props.dimY;
    const yearDiff = nextProps.year !== this.props.year;
    const typeDiff = nextProps.type !== this.props.type;
    const selectedDiff = nextProps.selected !== this.props.selected;

    return widthDifferent || heightDifferent || dataDifferent || dimYDiff || yearDiff || typeDiff || selectedDiff;
  }

  componentDidUpdate() {
    setTimeout(this.renderD3.bind(this), 100);
  }

  renderD3() {
    const width = this.props.width - this.props.margin.left - this.props.margin.right;
    const height = this.props.height - this.props.margin.top - this.props.margin.bottom;
    const props = this.props;

    if (!props.data || !props.data.length) {
      return;
    }

    const parseYear = d3.timeParse("%Y");
    const years = [2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015];

    const data = _.chain(props.data)
      .reduce((reducer, row) => {
        _.forEach(years, year => {
          const yearIndex = 2015 - (year || 2015);

          reducer.push({
            sa: row.sa,
            sn: row.sn,
            sf: row.sf,
            cf: row.cf,
            cn: row.cn,
            year: year,
            x: parseYear(year),
            y: _.get(row, [props.dimY, yearIndex], 0),
            g: _.get(row, props.dimG, 0)
          })
        });

        return reducer;
      }, [])
      .filter(d => d.y > 0)
      .value();

    const valueG = row => _.get(row, 'g');
    const valueX = row => _.get(row, 'x');
    const valueY = row => _.get(row, 'y');

    const valuesG = _.chain(data).map(valueG).uniq().value();
    const valuesX = _.chain(data).map(valueX).uniq().value();

    const x = d3.scaleTime()
      .domain(d3.extent(valuesX))
      .range([0, width]);

    const axisX = d3.axisBottom(x)
      .tickSizeInner(height, 0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(d3.timeYear);

    const y = d3.scaleLinear()
      .domain(d3.extent(data, valueY))
      .range([height, 0]);

    const axisY = d3.axisLeft(y)
      .tickSizeInner(width, 0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .tickFormat(formatKmbt)
      .ticks(6);

    const line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(valueX(d)))
      .y(d => y(valueY(d)));

    // Add the X Axis
    d3.select(this.refs.xAxis)
      .transition()
      .call(axisX);

    // Add the Y Axis
    d3.select(this.refs.yAxis)
      .transition()
      .call(axisY);

    // Add Lines
    const lines = d3.select(this.refs.lines)
      .selectAll("path")
      .data(_.reduce(valuesG, (output, value) => {
        output.push(_.filter(data, x => valueG(x) === value));
        return output;
      }, []));

    lines.enter().append("path").each(createLines);
    lines.each(createLines);
    lines.exit().remove();

    function createLines() {
      d3.select(this)
        .attr("class", () => {
          return props.type === 'x' ? 'line x' : 'line y';
        })
        .style("stroke-width", d => {
          return props.selected === _.get(d, [0, 'g']) ? 3 : 2;
        })
        .style("opacity", d => {
          return props.selected === _.get(d, [0, 'g']) ? 1 : 0.12;
        })
        .attr("d", function (d) {
          return line(d);
        })
    }

    // Add Hovers
    const hovers = d3.select(this.refs.hovers)
      .selectAll("path")
      .data(_.reduce(valuesG, (output, value) => {
        output.push(_.filter(data, x => valueG(x) === value));
        return output;
      }, []));

    hovers.enter().append("path").each(createHovers);
    hovers.each(createHovers);
    hovers.exit().remove();

    function createHovers() {
      d3.select(this)
        .attr("class", 'hover_out')
        .attr("id", d => "FIPS_" + _.get(d, [0, 'g']))
        .attr("d", function (d) {
          return line(d);
        })
    }

    // Add Dots
    const dots = d3.select(this.refs.dots)
      .selectAll("circle")
      .data(data);

    dots.enter().append("circle").each(createDots);
    dots.each(createDots);
    dots.exit().remove();

    function createDots() {
      d3.select(this)
        .attr("class", 'dot')
        .attr("r", 2)
        .attr("cx", d => x(valueX(d)))
        .attr("cy", d => y(valueY(d)))
        .on("mouseover", d => {
          const coords = [x(valueX(d)), y(valueY(d))];
          const content = (
            <div>
              <b className={props.type === 'x' ? 'red' : 'blue'}>{props.titleY} : </b>
              <i>{d3.format(',')(valueY(d))}</i><br/>
            </div>
          );

          if (props.onHoverOver) {
            props.onHoverOver(valueG(d), {coords, props: {}, data: {...d, content}})
          }
        })
        .on("mouseout", d => {
          if (props.onHoverOut) {
            props.onHoverOut(valueG(d))
          }
        })
        .on("click", d => {
          if (props.onClick) {
            props.onClick(valueG(d), d.year);
          }
        });
    }

    if (props.year) {
      d3.select(this.refs.highlight)
        .attr('class', 'highlight-bar')
        .attr('width', 2)
        .attr("rx", 1)
        .attr("ry", 1)
        .attr('height', height)
        .transition()
        .duration(300)
        .attr('transform', "translate(" + (x(parseYear(props.year)) - 1) + ",0)")
    }
  }

  render() {
    const innerWidth = this.props.width - this.props.margin.left - this.props.margin.right;

    // TODO can get better perf if we move some of these layers into new react modules with update logic
    return (
      <svg width={this.props.width} height={this.props.height}>
        <g transform={"translate(" + this.props.margin.left + "," + this.props.margin.top + ")"} ref="svg">
          <g className="axis">
            <g ref="xAxis"></g>
            <g ref="yAxis" transform={"translate(" + innerWidth + ",0)"}></g>
          </g>
          <g>
            <rect ref="highlight"></rect>
          </g>
          <g ref="lines"></g>
          <g ref="hovers"></g>
          <g ref="dots"></g>
          <g ref="legend" className="legend"></g>
        </g>
      </svg>
    );
  }
}

LineChartGrid.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dimG: PropTypes.string,
  dimY: PropTypes.string,
  year: PropTypes.number,
  data: PropTypes.array,
  selected: PropTypes.number,
  onHoverOver: PropTypes.func,
  onHoverOut: PropTypes.func
};

export default LineChartGrid;




// WEBPACK FOOTER //
// ./src/charts/LineChart/LineChartGrid.js